import React, { useEffect, useState } from 'react';
import { X, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AddressPicker from '../components/AddressPicker';

export default function ArticleEmailModal({ article, onClose }) {
  const auth = useAuth();
  const [to, setTo] = useState({ ids: [], emails: [] });
  const [subject, setSubject] = useState(article.subject || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!auth.users || auth.users.length === 0) auth.refreshUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    setErr(''); setResult(null);
    if (to.ids.length === 0 && to.emails.length === 0) { setErr('Select at least one recipient or add an address.'); return; }
    setBusy(true);
    try {
      const res = await auth.sendArticleEmail({ subject: subject || article.name, body: article.body || '', user_ids: to.ids, extra_emails: to.emails });
      setResult(res);
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.detail) || 'Could not send.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="article-email-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h3 className="font-title text-lg inline-flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Mail size={18} /> Email “{article.name}”</h3>
          <button onClick={onClose} data-testid="article-email-close"><X size={18} /></button>
        </div>

        {result ? (
          <div className="p-6 text-center" data-testid="article-email-result">
            <p className="font-title text-lg mb-1" style={{ color: '#1a7f37' }}>Sent to {result.sent} of {result.recipients} recipient{result.recipients === 1 ? '' : 's'}.</p>
            {result.mocked && <p className="text-sm" style={{ color: 'var(--maroon)' }}>Email is in MOCK mode — configure a provider in Admin → Email Settings to deliver for real.</p>}
            <button onClick={onClose} className="mt-4 px-4 py-2 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>Done</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="article-email-subject" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
            </div>

            <div>
              <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>To</label>
              <AddressPicker users={auth.users || []} memberClasses={auth.memberClasses || []} classLabel={auth.classLabel} value={to} onChange={setTo} testId="article-email-to" emailOnly />
              <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>Start typing to pick a tagged list (e.g. committee), a member class, an individual member, or type any email address and press Enter. Each recipient gets their own <code>{'{{fields}}'}</code> merged.</p>
            </div>

            {err && <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="article-email-error">{err}</p>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={send} disabled={busy} data-testid="article-email-send" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
